import { useState } from 'react';
import { LsCartThumb } from '@/components/ls-cart-thumb/LsCartThumb';
import { LsCta } from '@/components/ls-cta/LsCta';
import { LsStepHead } from '@/components/ls-step-head/LsStepHead';
import { useCartStore } from '@/features/cart/cart.store';
import { goToMiniapp } from '@/features/order/miniapp';
import { PARTNER_SLUG } from '@/shared/lib/partner';
import { fixHangers } from '@/shared/lib/typography';
import styles from './done-screen.module.scss';

interface Props {
  logoUrl?: string;
  /** Куда уводит «Перейти»: мини-приложение заказчика в телеграме. */
  miniappUrl?: string;
  /** Вернуться к выбору изделий. Пусто — кнопки не будет. */
  onBackToCatalog?: () => void;
  /**
   * Главное действие экрана. По умолчанию — уход в мини-приложение:
   * на сайте оформить заказ негде. В самом мини-приложении оформление
   * уже здесь, и действие ведёт на форму.
   */
  actionLabel?: string;
  onAction?: () => void;
  lead?: string;
}

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/**
 * Последний экран на сайте: заказ собран, дальше оформление в телеграме.
 *
 * Показываем **состав заказа**, а не одну футболку. Одна картинка отвечала
 * только на вопрос «как выглядит», а человеку на последнем шаге важно
 * другое: что именно он заказывает, сколько позиций и на какую сумму.
 * В корзине может лежать не одна вещь, и одна футболка про это врала.
 *
 * Форму заказа здесь не показываем и на неё не переводим: оформление
 * целиком живёт в мини-приложении, и собирать адрес дважды незачем.
 */
export function DoneScreen({
  logoUrl,
  miniappUrl,
  onBackToCatalog,
  actionLabel,
  onAction,
  lead,
}: Props) {
  const items = useCartStore((s) => s.items);
  const total = useCartStore((s) => s.total());
  const remove = useCartStore((s) => s.remove);
  const clear = useCartStore((s) => s.clear);

  // Адрес мини-приложения, куда ушёл заказ. Пока он есть, вместо
  // оформления стоит «спасибо»: заказ уже передан, и показывать ту же
  // корзину с той же кнопкой значит приглашать оформить его второй раз.
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const [isSending, setSending] = useState(false);
  const [isFailed, setFailed] = useState(false);

  // Корзина откладывается на сервере, а в бот уходит ссылка с ключом:
  // целиком она в ссылку не помещается, да и показывать её в адресной
  // строке незачем. Всё о передаче — в features/order/miniapp.
  const go = () => {
    if (onAction) {
      onAction();
      return;
    }

    if (isSending) return;
    setSending(true);
    setFailed(false);

    void goToMiniapp(PARTNER_SLUG, miniappUrl, items).then((url) => {
      setSending(false);
      if (!url) {
        setFailed(true);
        return;
      }
      // Корзина уже в черновике на сервере и приедет в мини-приложение.
      // Здесь она больше не нужна: следующая вещь начинается с чистой.
      clear();
      setHandoffUrl(url);
    });
  };

  const again = () => {
    setHandoffUrl(null);
    onBackToCatalog?.();
  };

  if (handoffUrl) {
    return (
      <div className={styles['ls-done']}>
        <LsStepHead
          step="Готово"
          hint="Заказ передан в телеграм"
          short="Готово"
          logoUrl={logoUrl}
        />
        <div className={[styles['ls-done__body'], styles['ls-done__body--is-thanks']].join(' ')}>
          <section className={styles['ls-done__hero']}>
            <h1 className={styles['ls-done__title']}>{fixHangers('Спасибо за заказ!')}</h1>
            <p className={styles['ls-done__lead']}>
              {fixHangers(
                'Мы открыли телеграм — там осталось указать адрес и оплатить. Если окно не появилось, нажми «Открыть телеграм».',
              )}
            </p>
            <div
              className={[styles['ls-done__foot'], styles['ls-done__foot--is-thanks']].join(' ')}
            >
              {/*
                Прямое нажатие на кнопку браузер не блокирует никогда —
                это запасной путь, если вкладка не открылась сама.
              */}
              <LsCta
                label="Открыть телеграм"
                onClick={() => window.open(handoffUrl, '_blank', 'noopener')}
                tone="inverse"
              />
              {onBackToCatalog && (
                <button className={styles['ls-done__again']} type="button" onClick={again}>
                  Собрать ещё
                </button>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['ls-done']}>
      <LsStepHead
        step="Шаг 3"
        hint="Оформление заказа"
        short="Оформление"
        priceLabel="Стоимость:"
        price={`${money.format(total)} ₽`}
        logoUrl={logoUrl}
      />

      <div className={styles['ls-done__body']}>
        <section className={styles['ls-done__hero']}>
          <h1 className={styles['ls-done__title']}>
            {fixHangers('Макет собран и готов к печати')}
          </h1>
          <p className={styles['ls-done__lead']}>
            {fixHangers(lead ?? 'Оформим заказ в телеграме')}
          </p>

          <div className={styles['ls-done__foot']}>
            {/*
              Без отдельного круга со стрелкой: рядом с широкой кнопкой он
              оставлял подпись прижатой к левому краю, и строка читалась
              как незаконченная. Подпись стоит по центру своей кнопки.
            */}
            {/*
              Подпись называет то, что человек получит, а не то, куда его
              перенесёт. «Перейти» отвечало на вопрос «куда я иду» —
              и на последнем шаге это единственный вопрос, который человек
              себе не задаёт.
            */}
            <LsCta
              label={actionLabel ?? 'Оплатить'}
              onClick={go}
              tone="inverse"
              isDisabled={items.length === 0 || isSending}
            />
          </div>
          {isFailed && (
            <p className={styles['ls-done__error']} role="alert">
              {fixHangers('Не получилось передать заказ в телеграм. Попробуй ещё раз.')}
            </p>
          )}
        </section>

        <aside className={styles['ls-done__cart']}>
          <p className={styles['ls-done__cart-head']}>Твой заказ</p>

          {items.length === 0 ? (
            <p className={styles['ls-done__empty']}>Корзина пуста</p>
          ) : (
            <ul className={styles['ls-done__list']}>
              {items.map((item) => (
                <li className={styles['ls-done__item']} key={item.id}>
                  <span className={styles['ls-done__thumb']}>
                    <LsCartThumb item={item} />
                  </span>

                  {/*
                    Название слева, цена справа, размер под ними. Строка
                    занимает всю ширину плашки: сведённые к середине
                    подписи оставляли справа пустое поле и читались
                    как обрезанные.
                  */}
                  <span className={styles['ls-done__info']}>
                    <span className={styles['ls-done__row']}>
                      <span className={styles['ls-done__name']}>{item.title}</span>
                      <span className={styles['ls-done__price']}>
                        {money.format(item.unitPriceRub * item.qty)} ₽
                      </span>
                    </span>

                    <span className={styles['ls-done__meta']}>
                      размер {item.size} · {item.qty} шт
                    </span>
                  </span>

                  <button
                    className={styles['ls-done__drop']}
                    type="button"
                    onClick={() => remove(item.id)}
                    aria-label={`Убрать ${item.title}`}
                  >
                    <span className={styles['ls-done__drop-icon']} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            Возврат к каталогу стоит в каждом месте, где человек видит
            состав заказа. Дособрать заказ он решает именно здесь,
            глядя на список, — и искать для этого полосу шагов не должен.
          */}
          {onBackToCatalog && (
            <button className={styles['ls-done__back']} type="button" onClick={onBackToCatalog}>
              Вернуться к каталогу
            </button>
          )}

          <p className={styles['ls-done__total']}>
            <span>Итого</span>
            <span>{money.format(total)} ₽</span>
          </p>
        </aside>
      </div>
    </div>
  );
}
